import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, ArrowLeftRight } from "lucide-react";
import { useDataStore, useDamageTableData, useWeaponByDescriptor } from "@/lib/store/data-store";
import type { Unit } from "@/lib/types/unit";
import type { WeaponData } from "@/lib/types/weapon";
import type { Side } from "@/lib/types/side";
import type { Motion } from "@/lib/types/motion";
import type { Veterancy, Cohesion } from "@/lib/types/veterancy-cohesion";
import type { DamageCalculationResult } from "@/lib/damage-calculator/damage-calculator-utils";
import { calculateDamage } from "@/lib/damage-calculator/damage-calculator-utils";
import { getWeaponsForUnit } from "@/lib/utils/unit-weapons";
import { UnitSelector } from "@/components/DamageCalculator/UnitSelector";
import { WeaponSelector } from "@/components/DamageCalculator/WeaponSelector";
import { DamageCalculatorControls } from "@/components/DamageCalculator/DamageCalculatorControls";
import { DamageCalculatorResults } from "@/components/DamageCalculator/DamageCalculatorResults";
import { ActiveTraitsDisplay } from "@/components/DamageCalculator/ActiveTraitsDisplay";
import { CompactUnitCard } from "@/components/UnitDetail/CompactUnitCard";
import { Side as SideEnum } from "@/lib/types/side";
import { Motion as MotionEnum } from "@/lib/types/motion";
import { MovementType } from "@/lib/types/unit";
import { RelatedTools } from "@/components/RelatedTools";

export function DamageCalculator() {
  const { unitId } = useParams<{ unitId?: string }>();
  const navigate = useNavigate();
  const {
    units,
    weapons,
    divisions,
    loadingUnits,
    loadingWeapons,
    getUnitByDescriptor,
  } = useDataStore();
  const { damageTable, loadingDamageTable } = useDamageTableData();

  const [sourceUnit, setSourceUnit] = useState<Unit | undefined>();
  const [targetUnit, setTargetUnit] = useState<Unit | undefined>();
  const [selectedWeaponIndexes, setSelectedWeaponIndexes] = useState<number[]>([0]);
  const [showUnitSelection, setShowUnitSelection] = useState(true);
  const [calculationResults, setCalculationResults] =
    useState<DamageCalculationResult[]>([]);
  const [individualWeaponResults, setIndividualWeaponResults] =
    useState<DamageCalculationResult[]>([]);

  // Get weapon data for source and target units
  const sourceWeaponData = useWeaponByDescriptor(sourceUnit?.weaponDescriptorName || "");
  const targetWeaponData = useWeaponByDescriptor(targetUnit?.weaponDescriptorName || "");

  // Calculation parameters
  const [distance, setDistance] = useState(0);
  const [side, setSide] = useState<Side>(SideEnum.FRONT);
  const [terrain, setTerrain] = useState("None");
  const [motion, setMotion] = useState<Motion>(MotionEnum.STATIC);
  const [sourceVeterancy, setSourceVeterancy] = useState<
    Veterancy | undefined
  >();
  const [targetVeterancy, setTargetVeterancy] = useState<
    Veterancy | undefined
  >();
  const [cohesion, setCohesion] = useState<Cohesion | undefined>();

  // Trait override state - Map of trait name to override state: true = forced on, false = forced off, undefined = use auto rules
  const [sourceTraitOverrides, setSourceTraitOverrides] = useState<Map<string, boolean | undefined>>(new Map());
  const [targetTraitOverrides, setTargetTraitOverrides] = useState<Map<string, boolean | undefined>>(new Map());

  // Get weapons for source unit
  const sourceWeapons = useMemo(() => {
    return getWeaponsForUnit(sourceUnit, weapons);
  }, [sourceUnit, weapons]);

  const selectedWeapons = useMemo(() => {
    return selectedWeaponIndexes
      .map((index) => sourceWeapons[index])
      .filter((weapon): weapon is WeaponData => weapon !== undefined);
  }, [sourceWeapons, selectedWeaponIndexes]);

  const divisionNameMap = useMemo(() => {
    const m = new Map<string, string>();
    (divisions || []).forEach((d) => m.set(d.descriptor, d.name));
    return m;
  }, [divisions]);

  const sourceDeckBuilderTargets = useMemo(() => {
    if (!sourceUnit?.divisions?.length) return undefined;
    return sourceUnit.divisions.map((d) => ({
      descriptor: d,
      name: divisionNameMap.get(d) ?? d,
    }));
  }, [sourceUnit, divisionNameMap]);

  // Calculate min/max range (considering all selected weapons)
  const { minRange, maxRange } = useMemo(() => {
    if (selectedWeapons.length === 0 || !targetUnit) {
      return { minRange: 0, maxRange: 0 };
    }

    const movementType = targetUnit.movementType;
    let max = 0;
    let min = Infinity;

    for (const weapon of selectedWeapons) {
      let weaponMax = 0;
      let weaponMin = 0;

      if (movementType === MovementType.LAND) {
        weaponMax = weapon.ammo.groundMaxRange || 0;
        weaponMin = weapon.ammo.groundMinRange || 0;
      } else if (movementType === MovementType.PLANE) {
        weaponMax = weapon.ammo.planeMaxRange || 0;
        weaponMin = weapon.ammo.planeMinRange || 0;
      } else if (movementType === MovementType.HELICOPTER) {
        weaponMax = weapon.ammo.heliMaxRange || 0;
        weaponMin = weapon.ammo.heliMinRange || 0;
      }

      max = Math.max(max, weaponMax);
      min = Math.min(min, weaponMin);
    }

    return { minRange: min === Infinity ? 0 : min, maxRange: max };
  }, [selectedWeapons, targetUnit]);

  // Initialize distance to max range when it changes
  useEffect(() => {
    if (maxRange > 0 && distance === 0) {
      setDistance(maxRange);
    } else if (maxRange > 0 && distance > maxRange) {
      setDistance(maxRange);
    }
  }, [maxRange, distance]);

  // Load initial unit if unitId is provided
  useEffect(() => {
    if (unitId && unitId !== "NONE" && !sourceUnit && units) {
      const unit = getUnitByDescriptor(unitId);
      if (unit) {
        setSourceUnit(unit);
      }
    }
  }, [unitId, sourceUnit, units, getUnitByDescriptor]);

  // Fetch data
  // Data is loaded at app start via DataStoreProvider
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Data loading handled by DataStoreProvider
  }, [
    units,
    weapons,
    damageTable,
    loadingUnits,
    loadingWeapons,
    loadingDamageTable,
  ]);

  // Reset weapon indexes when source unit changes
  useEffect(() => {
    setSelectedWeaponIndexes([0]);
  }, [sourceUnit?.descriptorName]);

  // Calculate damage when parameters change
  useEffect(() => {
    if (
      selectedWeapons.length > 0 &&
      sourceUnit &&
      targetUnit &&
      damageTable &&
      maxRange > 0
    ) {
      // Calculate combined damage (for main results)
      calculateDamage(
        selectedWeapons,
        sourceUnit,
        targetUnit,
        damageTable,
        distance,
        side,
        terrain,
        motion,
        sourceVeterancy,
        targetVeterancy,
        cohesion,
        sourceTraitOverrides,
        targetTraitOverrides
      ).then((result) => {
        if (result) {
          setCalculationResults([result]);
        } else {
          setCalculationResults([]);
        }
      });

      // Calculate individual weapon results (for comparison table)
      Promise.all(
        selectedWeapons.map((weapon) =>
          calculateDamage(
            [weapon],
            sourceUnit,
            targetUnit,
            damageTable,
            distance,
            side,
            terrain,
            motion,
            sourceVeterancy,
            targetVeterancy,
            cohesion,
            sourceTraitOverrides,
            targetTraitOverrides
          )
        )
      ).then((individualResults) => {
        setIndividualWeaponResults(
          individualResults.filter((r): r is DamageCalculationResult => r !== null)
        );
      });
    } else {
      setCalculationResults([]);
      setIndividualWeaponResults([]);
    }
  }, [
    selectedWeapons,
    sourceUnit,
    targetUnit,
    damageTable,
    distance,
    side,
    terrain,
    motion,
    sourceVeterancy,
    targetVeterancy,
    cohesion,
    maxRange,
    sourceTraitOverrides,
    targetTraitOverrides,
  ]);

  const swapUnits = () => {
    setShowUnitSelection(false);
    const temp = sourceUnit;
    setSourceUnit(targetUnit);
    setTargetUnit(temp);
    // Swap trait overrides as well
    const tempOverrides = sourceTraitOverrides;
    setSourceTraitOverrides(targetTraitOverrides);
    setTargetTraitOverrides(tempOverrides);
    setTimeout(() => {
      setShowUnitSelection(true);
    }, 0);
  };

  // Toggle trait override
  const toggleTraitOverride = (traitName: string, isSource: boolean) => {
    const overrides = isSource ? sourceTraitOverrides : targetTraitOverrides;
    const setOverrides = isSource ? setSourceTraitOverrides : setTargetTraitOverrides;
    
    const current = overrides.get(traitName);
    // Cycle: undefined -> true -> false -> undefined
    const newValue = current === undefined ? true : current === true ? false : undefined;
    
    const newMap = new Map(overrides);
    if (newValue === undefined) {
      newMap.delete(traitName);
    } else {
      newMap.set(traitName, newValue);
    }
    setOverrides(newMap);
  };

  if (loadingUnits || loadingWeapons || loadingDamageTable) {
    return (
      <div className="h-full overflow-y-auto bg-background">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          <Card className="border-dashed">
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center gap-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <div className="text-sm font-medium text-muted-foreground">
                  Loading damage calculator data...
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="w-full px-4 py-2">
        <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex w-fit items-center gap-1.5 hover:bg-muted h-8 px-2"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="text-sm">Back</span>
          </Button>
          {sourceUnit ? (
            <RelatedTools
              variant="compact"
              unitDescriptor={sourceUnit.descriptorName}
              deckBuilderTargets={sourceDeckBuilderTargets}
            />
          ) : null}
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Source Unit Card - Desktop only */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            <CompactUnitCard
              unit={sourceUnit}
              title="Source Unit"
              placeholder="Select a source unit"
              weaponData={sourceWeaponData}
              loadingWeapons={loadingWeapons}
            />
          </div>

          {/* Main Configuration Card */}
          <Card className="flex-1 shadow-lg">
            <CardHeader className="pb-3 border-b">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold">
                  Advanced Damage Calculator
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={swapUnits}
                  title="Swap units"
                  className="h-7 w-7 p-0 hover:bg-muted transition-colors"
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              {/* Unit Selection */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Source
                    </div>
                    {showUnitSelection && (
                      <UnitSelector
                        selectedUnit={sourceUnit}
                        onUnitSelect={(unit) => {
                          setSourceUnit(unit);
                          setSelectedWeaponIndexes([0]);
                        }}
                        placeholder="Search for source unit"
                      />
                    )}
                    {sourceWeapons.length > 0 && (
                      <div className="pt-2 border-t space-y-1.5">
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          Weapons
                        </div>
                        <WeaponSelector
                          weapons={sourceWeapons}
                          selectedWeaponIndexes={selectedWeaponIndexes}
                          onWeaponSelect={setSelectedWeaponIndexes}
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      Target
                    </div>
                    {showUnitSelection && (
                      <UnitSelector
                        selectedUnit={targetUnit}
                        onUnitSelect={setTargetUnit}
                        placeholder="Search for target unit"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Controls */}
              <DamageCalculatorControls
                weapon={selectedWeapons[0]}
                sourceUnit={sourceUnit}
                targetUnit={targetUnit}
                distance={distance}
                minRange={minRange}
                maxRange={maxRange}
                side={side}
                terrain={terrain}
                motion={motion}
                sourceVeterancy={sourceVeterancy}
                targetVeterancy={targetVeterancy}
                cohesion={cohesion}
                onDistanceChange={setDistance}
                onSideChange={setSide}
                onTerrainChange={setTerrain}
                onMotionChange={setMotion}
                onSourceVeterancyChange={setSourceVeterancy}
                onTargetVeterancyChange={setTargetVeterancy}
                onCohesionChange={setCohesion}
              />

              {/* Active Traits */}
              {(sourceUnit || targetUnit) && (
                <div className="space-y-4 pt-4 border-t">
                  <ActiveTraitsDisplay
                    sourceUnit={sourceUnit}
                    targetUnit={targetUnit}
                    distance={distance}
                    motion={motion}
                    sourceTraitOverrides={sourceTraitOverrides}
                    targetTraitOverrides={targetTraitOverrides}
                    onToggleTrait={toggleTraitOverride}
                    activeTraits={calculationResults[0]?.activeTraits}
                  />
                </div>
              )}

              {/* Results */}
              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold">Results</h2>
                </div>
                <DamageCalculatorResults
                  results={calculationResults}
                  weapons={selectedWeapons}
                  individualWeaponResults={individualWeaponResults}
                />
              </div>
            </CardContent>
          </Card>

          {/* Target Unit Card - Desktop only */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0">
            <CompactUnitCard
              unit={targetUnit}
              title="Target Unit"
              placeholder="Select a target unit"
              weaponData={targetWeaponData}
              loadingWeapons={loadingWeapons}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

